/**
 * VEDHACART Eats Menu - Deliveroo/UberEats Style
 * 
 * Features:
 * - Scroll-spy navigation
 * - Search & dietary filters
 * - Product modal with variants
 * - Add to cart
 * - Floating cart bar
 */

(function() {
  'use strict';

  // Config
  const config = window.EATS_MENU_CONFIG || {
    minimumOrder: 15,
    currency: '£'
  };

  // State
  let currentProduct = null;
  let currentVariant = null;
  let quantity = 1;
  let activeFilter = 'all';
  let searchQuery = '';

  // DOM Elements
  const elements = {
    nav: document.getElementById('menu-nav'),
    navItems: null,
    categories: null,
    items: null,
    searchInput: document.getElementById('menu-search'),
    filterBtns: document.querySelectorAll('.eatsmenu__filter-btn'),
    cartbar: document.getElementById('menu-cartbar'),
    cartCount: document.getElementById('cartbar-count'),
    cartTotal: document.getElementById('cartbar-total'),
    viewBasketBtn: document.getElementById('view-basket-btn'),
    modal: document.getElementById('product-modal'),
    noResults: document.getElementById('menu-no-results')
  };

  // Initialize
  function init() {
    elements.navItems = document.querySelectorAll('.eatsmenu__nav-item');
    elements.categories = document.querySelectorAll('.eatsmenu__category');
    elements.items = document.querySelectorAll('.eatsmenu-item');

    setupScrollSpy();
    setupNavigation();
    setupSearch();
    setupFilters();
    setupItemClicks();
    setupModal();
    setupCartBar();
    updateCartBar();
  }

  // ==========================================================================
  // Scroll Spy - Highlight current category in nav
  // ==========================================================================

  function setupScrollSpy() {
    if (!elements.categories.length) return;

    const navHeight = elements.nav?.offsetHeight || 52;
    const offset = navHeight + 20;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const categoryHandle = entry.target.dataset.category;
          setActiveNavItem(categoryHandle);
        }
      });
    }, {
      rootMargin: `-${offset}px 0px -70% 0px`,
      threshold: 0
    });

    elements.categories.forEach(cat => observer.observe(cat));
  }

  function setActiveNavItem(handle) {
    elements.navItems.forEach(item => {
      item.classList.toggle('is-active', item.dataset.category === handle);
    });

    // Scroll nav item into view
    const activeItem = document.querySelector(`.eatsmenu__nav-item[data-category="${handle}"]`);
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  // ==========================================================================
  // Navigation - Click to scroll
  // ==========================================================================

  function setupNavigation() {
    elements.navItems.forEach(item => {
      item.addEventListener('click', () => {
        const handle = item.dataset.category;
        const target = document.getElementById(`cat-${handle}`);
        
        if (target) {
          const navHeight = elements.nav?.offsetHeight || 52;
          const y = target.getBoundingClientRect().top + window.scrollY - navHeight - 10;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
    });
  }

  // ==========================================================================
  // Search
  // ==========================================================================

  function setupSearch() {
    if (!elements.searchInput) return;

    let debounceTimer;

    elements.searchInput.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        searchQuery = e.target.value.trim().toLowerCase();
        applyFilters();
      }, 200);
    });

    // Clear search button in no results
    const clearBtn = document.querySelector('.eatsmenu__clear-search');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        elements.searchInput.value = '';
        searchQuery = '';
        applyFilters();
      });
    }
  }

  // ==========================================================================
  // Dietary Filters
  // ==========================================================================

  function setupFilters() {
    elements.filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.filter;
        
        // Update active state
        elements.filterBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        
        activeFilter = filter;
        applyFilters();
      });
    });
  }

  function applyFilters() {
    let visibleCount = 0;
    const visibleCategories = new Set();

    elements.items.forEach(item => {
      const diet = item.dataset.diet || 'all';
      const title = item.dataset.title?.toLowerCase() || '';
      
      // Check diet filter
      let dietMatch = activeFilter === 'all' || diet === activeFilter;
      
      // Check search
      let searchMatch = !searchQuery || title.includes(searchQuery);
      
      const visible = dietMatch && searchMatch;
      item.style.display = visible ? '' : 'none';
      
      if (visible) {
        visibleCount++;
        // Find parent category
        const category = item.closest('.eatsmenu__category');
        if (category) {
          visibleCategories.add(category);
        }
      }
    });

    // Show/hide categories based on visible items
    elements.categories.forEach(cat => {
      const hasVisibleItems = visibleCategories.has(cat);
      cat.style.display = hasVisibleItems ? '' : 'none';
    });

    // Show/hide no results message
    if (elements.noResults) {
      elements.noResults.hidden = visibleCount > 0;
    }
  }

  // ==========================================================================
  // Item Clicks - Open Modal
  // ==========================================================================

  function setupItemClicks() {
    document.addEventListener('click', (e) => {
      const item = e.target.closest('.eatsmenu-item');
      const addBtn = e.target.closest('.eatsmenu-item__add-btn');
      
      if (!item) return;
      
      // Get product data
      const dataEl = item.querySelector('.eatsmenu-item__data');
      if (!dataEl) return;
      
      try {
        const productData = JSON.parse(dataEl.textContent);
        
        if (!productData.available) return;
        
        // If clicking add button and no variants, add directly
        if (addBtn && !productData.hasVariants) {
          e.stopPropagation();
          addToCart(productData.variants[0].id, 1);
          return;
        }
        
        // Open modal
        openModal(productData);
        
      } catch (err) {
        console.error('Failed to parse product data:', err);
      }
    });
  }

  // ==========================================================================
  // Product Modal
  // ==========================================================================

  function setupModal() {
    const modal = elements.modal;
    if (!modal) return;

    // Close button
    modal.querySelector('.eatsmenu-modal__close')?.addEventListener('click', closeModal);
    
    // Backdrop click
    modal.querySelector('.eatsmenu-modal__backdrop')?.addEventListener('click', closeModal);
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    // Quantity buttons
    modal.querySelectorAll('.eatsmenu-modal__qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        if (action === 'increase') {
          quantity = Math.min(quantity + 1, 99);
        } else if (action === 'decrease') {
          quantity = Math.max(quantity - 1, 1);
        }
        updateModalQuantity();
      });
    });

    // Add to basket button
    document.getElementById('modal-add-btn')?.addEventListener('click', () => {
      if (!currentVariant) return;
      
      const notes = document.getElementById('modal-notes')?.value || '';
      addToCart(currentVariant.id, quantity, notes);
      closeModal();
    });
  }

  function openModal(product) {
    const modal = elements.modal;
    if (!modal) return;

    currentProduct = product;
    quantity = 1;

    // Set basic info
    document.getElementById('modal-title').textContent = product.title;
    document.getElementById('modal-desc').textContent = product.description || '';
    
    // Image
    const imgContainer = document.getElementById('modal-image');
    if (product.image) {
      imgContainer.innerHTML = `<img src="${product.image}" alt="${product.title}">`;
      imgContainer.style.display = '';
    } else {
      imgContainer.style.display = 'none';
    }

    // Diet indicator
    const dietEl = document.getElementById('modal-diet');
    if (product.isVeg) {
      dietEl.setAttribute('data-diet', 'veg');
      dietEl.style.display = '';
    } else if (product.isNonVeg) {
      dietEl.setAttribute('data-diet', 'nonveg');
      dietEl.style.display = '';
    } else {
      dietEl.style.display = 'none';
    }

    // Build variant options
    buildVariantOptions(product);

    // Reset
    document.getElementById('modal-notes').value = '';
    document.getElementById('modal-qty').textContent = '1';

    // Select first variant
    if (product.variants.length > 0) {
      selectVariant(product.variants[0]);
    }

    // Show modal
    modal.hidden = false;
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    const modal = elements.modal;
    if (!modal) return;

    modal.hidden = true;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    currentProduct = null;
    currentVariant = null;
    quantity = 1;
  }

  function buildVariantOptions(product) {
    const container = document.getElementById('modal-options');
    container.innerHTML = '';

    if (!product.hasVariants || product.options.length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = '';

    product.options.forEach((option, optionIndex) => {
      // Skip default "Title" option
      if (option.name.toLowerCase() === 'title') return;

      const group = document.createElement('div');
      group.className = 'eatsmenu-modal__option-group';
      
      group.innerHTML = `
        <h4 class="eatsmenu-modal__option-title">
          ${option.name}
          <span class="eatsmenu-modal__option-required">Required</span>
        </h4>
        <div class="eatsmenu-modal__option-list" data-option-index="${optionIndex}">
          ${option.values.map((value, valueIndex) => {
            // Find variant with this option to get price
            const matchingVariant = product.variants.find(v => {
              const optKey = `option${optionIndex + 1}`;
              return v[optKey] === value;
            });
            
            const priceStr = matchingVariant ? matchingVariant.priceFormatted : '';
            
            return `
              <label class="eatsmenu-modal__option-item${valueIndex === 0 ? ' is-selected' : ''}" data-value="${value}">
                <input type="radio" name="option${optionIndex}" value="${value}" ${valueIndex === 0 ? 'checked' : ''}>
                <span class="eatsmenu-modal__option-radio"></span>
                <span class="eatsmenu-modal__option-label">${value}</span>
                ${priceStr ? `<span class="eatsmenu-modal__option-price">${priceStr}</span>` : ''}
              </label>
            `;
          }).join('')}
        </div>
      `;

      container.appendChild(group);

      // Add click handlers
      group.querySelectorAll('.eatsmenu-modal__option-item').forEach(item => {
        item.addEventListener('click', () => {
          // Update selected state
          group.querySelectorAll('.eatsmenu-modal__option-item').forEach(i => i.classList.remove('is-selected'));
          item.classList.add('is-selected');
          item.querySelector('input').checked = true;

          // Find matching variant
          findAndSelectVariant();
        });
      });
    });
  }

  function findAndSelectVariant() {
    if (!currentProduct) return;

    // Get selected options
    const selectedOptions = [];
    document.querySelectorAll('#modal-options .eatsmenu-modal__option-list').forEach((list, index) => {
      const checked = list.querySelector('input:checked');
      if (checked) {
        selectedOptions[index] = checked.value;
      }
    });

    // Find matching variant
    const variant = currentProduct.variants.find(v => {
      return selectedOptions.every((opt, index) => {
        return v[`option${index + 1}`] === opt;
      });
    });

    if (variant) {
      selectVariant(variant);
    }
  }

  function selectVariant(variant) {
    currentVariant = variant;
    
    // Update price display
    document.getElementById('modal-price').textContent = variant.priceFormatted;
    
    // Update add button
    updateModalTotal();
    
    // Update button state
    const addBtn = document.getElementById('modal-add-btn');
    if (addBtn) {
      addBtn.disabled = !variant.available;
      if (!variant.available) {
        addBtn.textContent = 'Sold out';
      }
    }
  }

  function updateModalQuantity() {
    document.getElementById('modal-qty').textContent = quantity;
    updateModalTotal();
  }

  function updateModalTotal() {
    if (!currentVariant) return;
    
    const total = (currentVariant.price * quantity / 100).toFixed(2);
    const totalEl = document.getElementById('modal-total');
    const addBtn = document.getElementById('modal-add-btn');
    
    if (totalEl) {
      totalEl.textContent = `${config.currency}${total}`;
    }
    
    if (addBtn && currentVariant.available) {
      addBtn.innerHTML = `Add to basket • <span>${config.currency}${total}</span>`;
    }
  }

  // ==========================================================================
  // Add to Cart
  // ==========================================================================

  async function addToCart(variantId, qty, notes = '') {
    try {
      const properties = {};
      if (notes) {
        properties['Special Instructions'] = notes;
      }

      const body = {
        items: [{
          id: variantId,
          quantity: qty,
          properties: properties
        }]
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body),
        credentials: 'same-origin'
      });

      if (!res.ok) {
        throw new Error('Failed to add to cart');
      }

      // Update cart bar
      await updateCartBar();

      // Show success feedback
      showToast('Added to basket!');

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('vc:cart:updated'));

    } catch (err) {
      console.error('Add to cart error:', err);
      showToast('Could not add to basket');
    }
  }

  // ==========================================================================
  // Floating Cart Bar
  // ==========================================================================

  function setupCartBar() {
    elements.viewBasketBtn?.addEventListener('click', () => {
      // Open cart drawer or go to cart page
      document.dispatchEvent(new CustomEvent('open-cart-drawer'));
      
      // Fallback: go to cart page
      // window.location.href = '/cart';
    });
  }

  async function updateCartBar() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await res.json();

      const count = cart.item_count || 0;
      const total = (cart.total_price / 100).toFixed(2);

      if (elements.cartCount) elements.cartCount.textContent = count;
      if (elements.cartTotal) elements.cartTotal.textContent = total;

      // Show/hide cart bar
      if (elements.cartbar) {
        // Cart bar always visible (acts as nav bar)
      // elements.cartbar.hidden = count === 0;
      }

      // Update header cart count too
      const headerCount = document.getElementById('cart-count');
      if (headerCount) headerCount.textContent = count;

    } catch (err) {
      console.error('Failed to update cart:', err);
    }
  }

  // ==========================================================================
  // Toast
  // ==========================================================================

  function showToast(message) {
    // Use existing toast or create simple one
    if (window.showToast) {
      window.showToast(message);
      return;
    }

    const toast = document.getElementById('toast');
    const textEl = document.getElementById('toast__text');
    
    if (toast && textEl) {
      textEl.textContent = message;
      toast.hidden = false;
      setTimeout(() => { toast.hidden = true; }, 2500);
    }
  }

  // ==========================================================================
  // Initialize on DOM Ready
  // ==========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for cart updates from other components
  document.addEventListener('vc:cart:updated', updateCartBar);
  document.addEventListener('cart:updated', updateCartBar);

})();
